/**
 * Storefront Theme Store
 * Manages theme presets (Classic, Modern, Minimal, Lite) and visitor/admin preferences.
 * Synced via localStorage key "khays_storefront_theme".
 */
import { create } from "zustand";
import { devtools } from "zustand/middleware";

const KEY = "khays_storefront_theme";

export type ThemePreset = "classic" | "modern" | "minimal" | "lite";
export type ModernIconStyle = "outline" | "filled" | "duotone";

export interface StorefrontThemeSettings {
  /** Admin-set default theme */
  adminDefault: ThemePreset;
  /** Whether visitors can toggle themes */
  visitorToggleEnabled: boolean;
  /** Themes disabled by admin (hidden from selection & storefront) */
  disabledThemes: ThemePreset[];
  /** Grading summary note (editable by admin, shown in notice area) */
  gradingSummaryNote: string;
  /** Category sort order (array of category IDs) */
  categorySortOrder: string[];
  /** Custom category icons (category -> URL) */
  categoryIcons: Record<string, string>;
  /** Whether device media gallery is sticky */
  mediaGallerySticky: boolean;
  /** Global default color mode: "light", "dark", or "system" */
  defaultColorMode: "light" | "dark" | "system";
  /** Modern theme font family (Google Fonts name, e.g. "Inter", "Space Grotesk") */
  modernFont: string;
  /** Modern theme icon visual style for bottom tabs */
  modernIconStyle: ModernIconStyle;
  /** Lite theme: master toggle for showing discount badges on cards */
  liteDiscountBadgesEnabled?: boolean;
  /** Lite theme: pull real device media into banners (vs. coloured placeholder) */
  liteBannerUseRealMedia?: boolean;
  /** Lite theme: per-banner colour override — keys: "deal", "featured" */
  liteBannerColors?: Record<string, string>;
  /** Lite theme: hide grades that are disabled/unavailable in the overlay (instead of showing them greyed-out). */
  liteHideDisabledGrades?: boolean;
  /** Lite theme: keep the overlay hero/media gallery pinned while scrolling (separate from the global mediaGallerySticky). */
  liteMediaGallerySticky?: boolean;
  /** Lite theme: optional grading summary note rendered below the grade chips in the overlay. Empty string hides it. */
  liteGradingSummaryNote?: string;
  /** Lite theme: master toggle — when OFF, no customer login UI renders anywhere. */
  liteCustomerAccountsEnabled?: boolean;
  /**
   * Lite theme: where to show inline color swatches + storage chips on the device card.
   *   • "always"    → on every card (matches reference exactly)
   *   • "adaptive"  → only when card is wide enough (>=180px)
   *   • "home-only" → only on the Home tab; Available Now / Offers grids stay compact
   */
  liteCardChipsMode?: "always" | "adaptive" | "home-only";
  /**
   * Lite theme: smart compact mode for device cards.
   *   • "expanded" → always full density (default)
   *   • "compact"  → always compact for every card
   *   • "auto"     → compact only when content volume / viewport demands it
   *   • "all"      → alias for "compact" (forces all cards compact)
   */
  liteCardCompactMode?: "expanded" | "compact" | "auto" | "all";
  /** Lite theme: allow visitors to flip Compact / Expanded from the storefront. */
  liteCardCompactVisitorToggle?: boolean;
}

function getDefaults(): StorefrontThemeSettings {
  return {
    // Lite is the platform default. Non-Lite themes are gated behind the
    // owner-only `khays_themes_enabled` flag (see src/lib/themesEnabledFlag.ts).
    adminDefault: "lite",
    visitorToggleEnabled: false,
    // All non-Lite themes are disabled by default. The owner can re-enable
    // them per tenant/main-store from the admin dashboard.
    disabledThemes: ["classic", "modern", "minimal"],
    gradingSummaryNote: "All devices are professionally inspected and graded. Tap on a grade to learn more about what each condition means.",
    categorySortOrder: [],
    categoryIcons: {},
    mediaGallerySticky: true,
    defaultColorMode: "light",
    modernFont: "Inter",
    modernIconStyle: "outline",
    liteDiscountBadgesEnabled: true,
    liteBannerUseRealMedia: true,
    liteBannerColors: { deal: "#F97316", featured: "#2563EB" },
    liteHideDisabledGrades: false,
    liteMediaGallerySticky: true,
    liteGradingSummaryNote: "",
    liteCustomerAccountsEnabled: false,
    liteCardChipsMode: "adaptive",
    liteCardCompactMode: "auto",
    liteCardCompactVisitorToggle: true,
  };
}

function loadFromStorage(): StorefrontThemeSettings {
  try {
    const stored = localStorage.getItem(KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      return { ...getDefaults(), ...parsed };
    }
  } catch {}
  return getDefaults();
}

interface ThemeState extends StorefrontThemeSettings {
  /** Visitor's chosen theme (overrides admin default) */
  visitorTheme: ThemePreset | null;
  setSettings: (s: StorefrontThemeSettings) => void;
  patch: (p: Partial<StorefrontThemeSettings>) => void;
  setVisitorTheme: (t: ThemePreset | null) => void;
  reload: () => void;
}

const VISITOR_KEY = "khays_visitor_theme";

export const useStorefrontTheme = create<ThemeState>()(
  devtools(
    (set, get) => ({
      ...loadFromStorage(),
      visitorTheme: (() => {
        try { return localStorage.getItem(VISITOR_KEY) as ThemePreset | null; } catch { return null; }
      })(),

      setSettings: (s) => {
        localStorage.setItem(KEY, JSON.stringify(s));
        set({ ...s });
      },

      patch: (p) => {
        const { setSettings, patch, reload, setVisitorTheme, visitorTheme, ...current } = get();
        const next = { ...current, ...p };
        localStorage.setItem(KEY, JSON.stringify(next));
        set(p);
      },

      setVisitorTheme: (t) => {
        if (t) localStorage.setItem(VISITOR_KEY, t);
        else localStorage.removeItem(VISITOR_KEY);
        set({ visitorTheme: t });
      },

      reload: () => {
        set({
          ...loadFromStorage(),
          visitorTheme: (() => {
            try { return localStorage.getItem(VISITOR_KEY) as ThemePreset | null; } catch { return null; }
          })(),
        });
      },
    }),
    { name: "StorefrontTheme" }
  )
);

/** Get the active theme for the storefront */
export function getActiveTheme(): ThemePreset {
  const state = useStorefrontTheme.getState();
  if (state.visitorToggleEnabled && state.visitorTheme) return state.visitorTheme;
  return state.adminDefault;
}
